import { useState, useEffect, useMemo } from 'react';
import {
  MapSpritePicker,
  sheetOptions,
  sheetUrl,
  useImageCatalog,
} from '../../components/SpritePicker.js';
import {
  fetchAdminContent,
  saveAdminContent,
  deleteAdminContent,
  type AdminItem,
} from '../../admin/contentApi.js';
import { ContentOrigin } from '../../admin/ContentOrigin.js';

export interface CharacterDef {
  readonly id: number;
  readonly name: string;
  readonly classId: number;
  readonly level: number;
  readonly portrait: { sheet: string; index: number };
  readonly mapSprite: { sheet: string; index: number };
  readonly battler: string;
  readonly equipment: number[];
  readonly note?: string;
}

interface MiniClass {
  readonly id: number;
  readonly name: string;
}

interface MiniWeapon {
  readonly id: number;
  readonly name: string;
  readonly type: string;
}

/*
 * Listas de reserva, e não o catálogo.
 *
 * O catálogo vem do manifesto, que sabe o que existe em disco — inclusive as
 * folhas que os jogos importados acrescentaram. Estes nomes cobrem só o
 * intervalo até o manifesto chegar, e o caso em que ele não chega: sem eles a
 * lista abriria vazia e o editor não deixaria escolher nada.
 */
const AVAILABLE_FACES = [
  'Distortidus',
  'Mondona',
  'Actor1',
  'Actor2',
  'Actor3',
  'Evil',
  'Monster',
  'Nature',
  'People1',
  'People2',
  'People3',
  'People4',
  'SF_Actor1',
  'SF_Actor2',
  'SF_Actor3',
  'SF_Monster',
  'SF_People1',
  'team-naux',
];

const AVAILABLE_BATTLERS = [
  'distortidus',
  'mondona',
  'Actor1_1',
  'Actor1_2',
  'Actor1_3',
  'Actor1_4',
  'Actor1_5',
  'Actor1_6',
  'Actor1_7',
  'Actor1_8',
  'Actor2_1',
  'Actor2_2',
  'Actor2_3',
  'Actor2_4',
  'Actor2_5',
  'Actor2_6',
  'Actor2_7',
  'Actor2_8',
  'Actor3_5',
  'Actor3_6',
  'Actor3_7',
  'Actor3_8',
  'Anjo-do-mal',
  'Clodz',
  'Dulpbuk',
  'SF_Actor1_1',
  'SF_Actor1_2',
  'team-naux-lutando',
  'team-naux-lutando2',
  'team-naux-lutando3',
  'team-naux-lutando4',
];

export function CharacterEditor(): JSX.Element {
  const [items, setItems] = useState<AdminItem<CharacterDef>[]>([]);
  const [classes, setClasses] = useState<MiniClass[]>([]);
  const [weapons, setWeapons] = useState<MiniWeapon[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [classId, setClassId] = useState<number>(1);
  const [level, setLevel] = useState<number>(1);
  const [portraitSheet, setPortraitSheet] = useState('Distortidus');
  const [portraitIndex, setPortraitIndex] = useState(0);
  const [mapSpriteSheet, setMapSpriteSheet] = useState('distortidus');
  const [mapSpriteIndex, setMapSpriteIndex] = useState(0);
  const [battler, setBattler] = useState('distortidus');
  const [equipment, setEquipment] = useState<number[]>([]);
  const [note, setNote] = useState('');
  const [isNew, setIsNew] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [charData, classData, weaponData] = await Promise.all([
        fetchAdminContent<CharacterDef>('characters'),
        fetchAdminContent<MiniClass>('classes'),
        fetchAdminContent<MiniWeapon>('weapons'),
      ]);
      setItems(charData);
      setClasses(classData.map((c) => ({ id: c.contentId, name: c.name })));
      setWeapons(weaponData.map((w) => ({ id: w.contentId, name: w.name, type: (w.data as any).type || 'espada' })));
      if (charData.length > 0 && selectedId === null && charData[0]) {
        selectItem(charData[0]);
      }
    } catch (e) {
      setMessage({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const selectItem = (item: AdminItem<CharacterDef>) => {
    setSelectedId(item.contentId);
    setIsNew(false);
    setName(item.name || item.data.name || '');
    setClassId(item.data.classId ?? 1);
    setLevel(item.data.level ?? 1);
    setPortraitSheet(item.data.portrait?.sheet || 'Distortidus');
    setPortraitIndex(item.data.portrait?.index ?? 0);
    setMapSpriteSheet(item.data.mapSprite?.sheet || 'distortidus');
    setMapSpriteIndex(item.data.mapSprite?.index ?? 0);
    setBattler(item.data.battler || 'distortidus');
    setEquipment([...(item.data.equipment || [])]);
    setNote(item.data.note ?? '');
    setMessage(null);
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsNew(true);
    setName('Novo Recruta');
    setClassId(classes[0]?.id ?? 1);
    setLevel(1);
    setPortraitSheet('Actor1');
    setPortraitIndex(0);
    setMapSpriteSheet('Actor1');
    setMapSpriteIndex(0);
    setBattler('Actor1_1');
    setEquipment(weapons[0] ? [weapons[0].id] : []);
    setNote('Um guerreiro valoroso que se junta ao exército.');
    setMessage(null);
  };

  const handleEquipmentChange = (slot: number, weaponId: number | null) => {
    const next = [...equipment];
    if (weaponId === null) {
      next.splice(slot, 1);
    } else {
      next[slot] = weaponId;
    }
    setEquipment(next.filter((id) => id > 0));
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ type: 'err', text: 'O nome do personagem é obrigatório.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    /*
     * O que o formulário não mostra tem de sobreviver ao salvamento.
     *
     * A gravação substitui o registro inteiro pelo que sobe daqui, e há campos
     * que este formulário não edita — de qual jogo importado o registro veio,
     * de qual folha o ícone fala, e o que o conversor trouxe da origem.
     * Montando o pacote em cima da ficha atual, um ajuste de preço deixa de
     * apagá-los.
     */
    const existing = items.find((it) => it.contentId === selectedId)?.data;

    const payload: Partial<CharacterDef> & { name: string } = {
      ...(isNew || !existing ? {} : existing),
      name: name.trim(),
      classId: Number(classId),
      level: Number(level),
      portrait: { sheet: portraitSheet, index: Number(portraitIndex) },
      mapSprite: { sheet: mapSpriteSheet, index: Number(mapSpriteIndex) },
      battler,
      equipment,
      note,
    };

    const targetId = isNew ? null : selectedId;
    const res = await saveAdminContent<CharacterDef>('characters', targetId, payload);

    setSaving(false);
    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao salvar personagem.' });
    } else {
      setMessage({ type: 'ok', text: `Personagem "${name}" salvo com sucesso!` });
      await loadData();
      if (res.item) {
        selectItem(res.item);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Tem certeza que deseja excluir o personagem ID #${selectedId} (${name})?`)) {
      return;
    }

    setSaving(true);
    const res = await deleteAdminContent('characters', selectedId);
    setSaving(false);

    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao excluir personagem.' });
    } else {
      setMessage({ type: 'ok', text: `Personagem #${selectedId} excluído.` });
      const remaining = items.filter((it) => it.contentId !== selectedId);
      setItems(remaining);
      if (remaining.length > 0 && remaining[0]) {
        selectItem(remaining[0]);
      } else {
        handleNew();
      }
    }
  };

  const filteredItems = items.filter(
    (it) =>
      it.name.toLowerCase().includes(filterText.toLowerCase()) ||
      String(it.contentId).includes(filterText),
  );

  const currentItem = items.find((it) => it.contentId === selectedId);

  // Calcula offsets de visualização da face (grid 4 colunas x 2 linhas)
  const faceCol = portraitIndex % 4;
  const faceRow = Math.floor(portraitIndex / 4);

  const faces = useImageCatalog('faces');
  const battlers = useImageCatalog('sv_actors');
  const faceNames = useMemo(
    () => sheetOptions(faces, portraitSheet, AVAILABLE_FACES),
    [faces, portraitSheet],
  );
  const battlerNames = useMemo(
    () => sheetOptions(battlers, battler, AVAILABLE_BATTLERS),
    [battlers, battler],
  );

  return (
    <div className="admin-db-container">
      {/* Coluna Esquerda: Lista de Personagens */}
      <aside className="admin-sidebar-list">
        <div className="admin-list-header">
          <input
            type="text"
            className="admin-search-input"
            placeholder="🔍 Filtrar recrutas..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <button type="button" className="admin-btn-primary admin-btn-new" onClick={handleNew}>
            + Novo Personagem
          </button>
        </div>

        {loading ? (
          <div className="admin-loading-indicator">Carregando personagens…</div>
        ) : (
          <div className="admin-items-scroll">
            {filteredItems.map((it) => {
              const active = !isNew && it.contentId === selectedId;
              const className = classes.find((c) => c.id === it.data.classId)?.name ?? 'Sem classe';
              return (
                <button
                  key={it.contentId}
                  type="button"
                  className={`admin-list-item-btn ${active ? 'active' : ''}`}
                  onClick={() => selectItem(it)}
                >
                  <span className="admin-item-id">
                    {String(it.contentId).padStart(3, '0')}:
                  </span>
                  <span className="admin-item-name">{it.name}</span>
                  {it.isCustom && <span className="admin-badge-custom">Custom</span>}
                  <span className="admin-item-meta">
                    Nv.{it.data.level ?? 1} · {className}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      {/* Coluna Direita: Detalhes do Personagem */}
      <main className="admin-main-form">
        <div className="admin-form-top-bar">
          <div className="admin-form-title-area">
            <h2>
              {isNew ? '✨ Criando Novo Personagem' : `👥 Personagem #${selectedId}: ${name}`}
            </h2>
            {currentItem && (
              <ContentOrigin item={currentItem} />
            )}
          </div>

          <div className="admin-form-actions">
            {!isNew && (
              <button
                type="button"
                className="admin-btn-danger"
                onClick={() => void handleDelete()}
                disabled={saving}
              >
                🗑️ Excluir
              </button>
            )}
            <button
              type="button"
              className="admin-btn-save"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Salvando…' : '💾 Salvar Alterações'}
            </button>
          </div>
        </div>

        {message && (
          <div className={`admin-alert-banner ${message.type === 'ok' ? 'success' : 'error'}`}>
            {message.text}
          </div>
        )}

        <div className="admin-form-body-grid">
          {/* Caixa 1: Identidade & Classe */}
          <div className="admin-section-box">
            <h3 className="admin-section-title">Identidade & Perfil</h3>

            <div className="admin-form-row">
              <label>Nome do Personagem:</label>
              <input
                type="text"
                className="admin-input-text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Sir Lancelot"
              />
            </div>

            <div className="admin-grid-2col">
              <div className="admin-form-row">
                <label>Classe Inicial:</label>
                <select
                  className="admin-select"
                  value={classId}
                  onChange={(e) => setClassId(Number(e.target.value))}
                >
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      #{c.id}: {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="admin-form-row">
                <label>Nível Inicial:</label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  className="admin-input-number"
                  value={level}
                  onChange={(e) => setLevel(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="admin-form-row">
              <label>Biografia / Notas:</label>
              <textarea
                className="admin-textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Histórico do guerreiro, alianças ou características marcantes."
              />
            </div>
          </div>

          {/* Caixa 2: Gráficos e Battlers (com Preview!) */}
          <div className="admin-section-box">
            <h3 className="admin-section-title">Aparência Visual & Battlers</h3>

            <div className="admin-graphics-preview-row">
              {/* Preview Face */}
              <div className="admin-preview-card">
                <span className="admin-preview-label">Retrato de Diálogo (Face)</span>
                <div
                  className="admin-face-preview-box"
                  style={{
                    backgroundImage: `url(${sheetUrl(faces, 'faces', portraitSheet)})`,
                    backgroundSize: '400% 200%',
                    backgroundPosition: `${faceCol * 33.333}% ${faceRow * 100}%`,
                  }}
                />
                <div className="admin-preview-selectors">
                  <select
                    className="admin-select-small"
                    value={portraitSheet}
                    onChange={(e) => setPortraitSheet(e.target.value)}
                  >
                    {faceNames.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                  <select
                    className="admin-select-small"
                    value={portraitIndex}
                    onChange={(e) => setPortraitIndex(Number(e.target.value))}
                  >
                    {[0, 1, 2, 3, 4, 5, 6, 7].map((idx) => (
                      <option key={idx} value={idx}>
                        Índice {idx}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview Battler */}
              <div className="admin-preview-card">
                <span className="admin-preview-label">Battler de Combate Lateral</span>
                <div
                  className="admin-battler-preview-box"
                  style={{
                    backgroundImage: `url(${sheetUrl(battlers, 'sv_actors', battler)})`,
                    backgroundSize: '900% 600%',
                    backgroundPosition: '0 0',
                  }}
                />
                <div className="admin-preview-selectors">
                  <select
                    className="admin-select-small"
                    value={battler}
                    onChange={(e) => setBattler(e.target.value)}
                  >
                    {battlerNames.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview do sprite que anda no tabuleiro */}
              <MapSpritePicker
                label="Sprite no Mapa (Tabuleiro)"
                value={{ sheet: mapSpriteSheet, index: mapSpriteIndex }}
                onChange={(next) => {
                  setMapSpriteSheet(next.sheet);
                  setMapSpriteIndex(next.index);
                }}
              />
            </div>

            <p className="admin-helper-text" style={{ marginTop: '12px' }}>
              O retrato aparece nos diálogos, o battler no combate lateral e o sprite de mapa é a
              peça que caminha pelo tabuleiro durante a escaramuça.
            </p>
          </div>

          {/* Caixa 3: Equipamento Inicial */}
          <div className="admin-section-box admin-section-span2">
            <h3 className="admin-section-title">Bolsa Inicial (até 4 armas)</h3>
            <p className="admin-helper-text">
              A bolsa com que o personagem entra no esquadrão da campanha e do online: a primeira
              arma que a classe usa vira a titular, as outras vêm atrás, e a poção tática entra se
              couber. A escaramuça, que é teste, parte só da titular e completa a bolsa sozinha. As
              armaduras do RPG ficam guardadas à parte e não têm efeito — o motor não tem armadura.
            </p>

            <div className="admin-inventory-slots-grid">
              {[0, 1, 2, 3].map((slotIdx) => {
                const currentEquipId = equipment[slotIdx] ?? 0;
                return (
                  <div key={slotIdx} className="inventory-slot-card">
                    <span className="inventory-slot-tag">Slot #{slotIdx + 1}</span>
                    <select
                      className="admin-select"
                      value={currentEquipId}
                      onChange={(e) => {
                        const val = Number(e.target.value);
                        handleEquipmentChange(slotIdx, val > 0 ? val : null);
                      }}
                    >
                      <option value="0">(Vazio / Sem Item)</option>
                      {weapons.map((w) => (
                        <option key={w.id} value={w.id}>
                          #{w.id}: {w.name} [{w.type.toUpperCase()}]
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
